import { AdminPageIntroConfig } from "./AdminPageIntro";

const intro = (
  title: string,
  summary: string,
  breadcrumbs: AdminPageIntroConfig["breadcrumbs"],
  eyebrow: string = "Content Management"
): AdminPageIntroConfig => ({
  eyebrow,
  title,
  summary,
  breadcrumbs,
});

const pageIntroConfig: Array<{ match: (path: string) => boolean; intro: AdminPageIntroConfig }> = [
  {
    match: (path) => path === "/content/menus",
    intro: intro("Menus", "Organise menu groups, maintain navigation items, and keep site navigation consistent across languages and layouts.", [
      { title: "Content", url: "/content" },
      { title: "Menus" },
    ]),
  },
  {
    match: (path) => path === "/content/headers",
    intro: intro("Site Headers", "Manage reusable page headers, assign them to site languages, and keep the site presentation consistent across your pages.", [
      { title: "Content", url: "/content" },
      { title: "Headers" },
    ]),
  },
  {
    match: (path) => path === "/content/footers",
    intro: intro("Site Footers", "Manage reusable page footers, assign them to site languages, and keep the lower-page content consistent across the site.", [
      { title: "Content", url: "/content" },
      { title: "Footers" },
    ]),
  },
  {
    match: (path) => path === "/content/privacynotices",
    intro: intro("Privacy Notices", "Manage privacy and cookie notices by language, control where they appear, and keep consent messaging consistent across the site.", [
      { title: "Content", url: "/content" },
      { title: "Privacy Notices" },
    ]),
  },
  {
    match: (path) => path === "/content/scriptandcss",
    intro: intro("Scripts and Styles", "Manage custom stylesheets, JavaScript, robots content, and AI-facing files that support the site experience.", [
      { title: "Content", url: "/content" },
      { title: "Scripts & Styles" },
    ]),
  },
  {
    match: (path) => path === "/content/languages",
    intro: intro("Site Languages", "Configure the languages available on the site so editors can create, localise, and manage content confidently.", [
      { title: "Content", url: "/content" },
      { title: "Languages" },
    ]),
  },
  {
    match: (path) => path === "/content/mediamanager",
    intro: intro("Media Manager", "Upload, organise, and manage reusable media files and folders for editors across the site.", [
      { title: "Content", url: "/content" },
      { title: "Media Manager" },
    ]),
  },
  {
    match: (path) => path.startsWith("/content/pagekeyword"),
    intro: intro("Page Keywords", "Maintain search keywords against site pages so editorial teams can support discovery and categorisation workflows.", [
      { title: "Content", url: "/content" },
      { title: "Page Keywords" },
    ]),
  },
  {
    match: (path) => path.startsWith("/content/metadata"),
    intro: intro("Page Metadata", "Manage page-level metadata entries for search, integrations, and site-level presentation requirements.", [
      { title: "Content", url: "/content" },
      { title: "Page Metadata" },
    ]),
  },
  {
    match: (path) => path === "/content/sitemetadata",
    intro: intro("Site Metadata", "Configure shared site metadata by language so global search and integration settings stay aligned.", [
      { title: "Content", url: "/content" },
      { title: "Site Metadata" },
    ]),
  },
  {
    match: (path) => path === "/content/manageredirects",
    intro: intro("Redirects", "Manage controlled URL redirects so content changes do not break inbound links, campaigns, or indexed routes.", [
      { title: "Content", url: "/content" },
      { title: "Redirects" },
    ]),
  },
  {
    match: (path) => path === "/content/socialmedia",
    intro: intro("Social Media", "Connect content workflows to outbound social channels and manage the content prepared for social publishing.", [
      { title: "Content", url: "/content" },
      { title: "Social Media" },
    ]),
  },
  {
    match: (path) => path === "/content/aihelper",
    intro: intro("AI Helper", "Explore the AI integration workspace for structured content generation, rule-driven prompts, and future draft automation.", [
      { title: "Content", url: "/content" },
      { title: "AI Helper" },
    ]),
  },
  {
    match: (path) => path === "/content/staticroutes",
    intro: intro("Static Routes", "Review and manage static route definitions that support special pages and route-level site behaviour.", [
      { title: "Content", url: "/content" },
      { title: "Static Routes" },
    ]),
  },
  {
    match: (path) => path === "/content/submitteddata",
    intro: intro("Submitted Data", "Search and review submitted form data so teams can track responses, exports, and operational follow-up.", [
      { title: "Content", url: "/content" },
      { title: "Submitted Data" },
    ]),
  },
  {
    match: (path) => path === "/content/socialmedialinks",
    intro: intro("Social Media Links", "Manage the social profiles and channel links exposed across the site and related content surfaces.", [
      { title: "Content", url: "/content" },
      { title: "Social Media Links" },
    ]),
  },
  {
    match: (path) => path === "/subscribers/manage",
    intro: intro("Subscribers", "Review, edit, and manage the subscribers attached to this site, including newsletter and availability settings.", [
      { title: "Content", url: "/content" },
      { title: "Subscribers" },
    ], "Subscriber Management"),
  },
  {
    match: (path) => path === "/subscribers/import",
    intro: intro("Import Subscribers", "Bring subscriber data into the site in bulk and prepare mailing audiences more efficiently.", [
      { title: "Content", url: "/content" },
      { title: "Subscribers", url: "/subscribers/manage" },
      { title: "Import" },
    ], "Subscriber Management"),
  },
  {
    match: (path) => path === "/subscribers/newsletters",
    intro: intro("Newsletters", "Create and manage newsletter campaigns for site subscribers and recurring audience communications.", [
      { title: "Content", url: "/content" },
      { title: "Subscribers", url: "/subscribers/manage" },
      { title: "Newsletters" },
    ], "Subscriber Management"),
  },
  {
    match: (path) => path === "/users/all",
    intro: intro("Site Users", "Manage the users who can access and administer this site, including their status and assigned role type.", [
      { title: "Content", url: "/content" },
      { title: "Users" },
    ], "User Management"),
  },
  {
    match: (path) => path === "/users/mydetails",
    intro: intro("My Details", "Review and maintain your user profile details for this site administration account.", [
      { title: "Content", url: "/content" },
      { title: "Users", url: "/users/all" },
      { title: "My Details" },
    ], "User Management"),
  },
  {
    match: (path) => path === "/users/changepassword",
    intro: intro("Change Password", "Update your password and keep access to the administration area secure.", [
      { title: "Content", url: "/content" },
      { title: "Users", url: "/users/all" },
      { title: "Change Password" },
    ], "User Management"),
  },
  {
    match: (path) => path === "/users/registernewsite",
    intro: intro("Register New Site", "Create and configure a new site so it can be managed as part of the wider product suite.", [
      { title: "Content", url: "/content" },
      { title: "Users", url: "/users/all" },
      { title: "Register New Site" },
    ], "User Management"),
  },
  {
    match: (path) => path === "/account/details",
    intro: intro("Account Details", "Review and manage account-level details that support site ownership, billing, and administrative access.", [
      { title: "Content", url: "/content" },
      { title: "Settings" },
      { title: "Account Details" },
    ], "Account Settings"),
  },
  {
    match: (path) => path === "/account/sitedetails",
    intro: intro("Site Details", "Manage the primary details for this site, including configuration used across the CMS and connected services.", [
      { title: "Content", url: "/content" },
      { title: "Settings" },
      { title: "Site Details" },
    ], "Account Settings"),
  },
  {
    match: (path) => path === "/systemadmin/components",
    intro: intro("Components", "Manage reusable content components and the fields editors use when building pages and layouts.", [
      { title: "Content", url: "/content" },
      { title: "Components" },
    ], "System Administration"),
  },
  {
    match: (path) => path === "/systemadmin/propertytypes",
    intro: intro("Component Property Types", "Manage the property type definitions available to components across the CMS.", [
      { title: "Content", url: "/content" },
      { title: "Property Types" },
    ], "System Administration"),
  },
  {
    match: (path) => path === "/systemadmin/layouttemplates",
    intro: intro("Layout Templates", "Maintain the layout templates available to designers and editors when building pages.", [
      { title: "Content", url: "/content" },
      { title: "Layout Templates" },
    ], "System Administration"),
  },
];

export const getAdminPageIntroConfig = (pathname: string): AdminPageIntroConfig | null => {
  const normalisedPath = pathname.toLowerCase();
  const matched = pageIntroConfig.find((item) => item.match(normalisedPath));
  return matched?.intro ?? null;
};
